import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Info } from 'lucide-react';

const SensorVisualization = ({ data }) => {
  const [visibleFields, setVisibleFields] = useState({
    uncorrected_inner: true,
    uncorrected_outer: true,
    battery_voltage: true
  });

  const formatXAxis = (tickItem) => {
    const date = new Date(tickItem);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const toggleField = (field) => {
    setVisibleFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const sortedData = [...data].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  return (
    <div className="mt-6">
      <div className="flex flex-col space-y-4">
        <h3 className="text-xl font-semibold text-green-700">Sensor Data Visualization</h3>

        <div className="flex items-start space-x-2 bg-blue-50 p-4 rounded-lg text-sm text-gray-600">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p>
            This graph shows the relationship between uncorrected sap flow measurements and battery voltage over time. 
            Toggle different metrics using the buttons below to focus on specific measurements.
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => toggleField('uncorrected_inner')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              visibleFields.uncorrected_inner
                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            Uncorrected Inner Sap Flow
          </button>
          <button
            onClick={() => toggleField('uncorrected_outer')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              visibleFields.uncorrected_outer
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            Uncorrected Outer Sap Flow
          </button>
          <button
            onClick={() => toggleField('battery_voltage')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              visibleFields.battery_voltage
                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            Battery Voltage
          </button>
        </div>

        <ResponsiveContainer width="100%" height={500}>
          <LineChart
            data={sortedData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 120,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="timestamp" 
              tickFormatter={formatXAxis} 
              interval={Math.ceil(sortedData.length / 10)} 
              angle={-45} 
              textAnchor="end" 
              height={80}
              tick={{fontSize: 10}}
            />
            <YAxis />
            <Tooltip 
              labelFormatter={formatXAxis}
              formatter={(value, name) => {
                if (name === "Battery Voltage (V)") {
                  return [value.toFixed(2) + " V", name];
                }
                return [value.toFixed(3) + " cm/hr", name];
              }}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36} 
              wrapperStyle={{paddingTop: "20px"}}
            />
            {visibleFields.uncorrected_inner && (
              <Line 
                type="monotone" 
                dataKey="uncorrected_inner" 
                stroke="#8884d8" 
                activeDot={{ r: 3 }}
                dot={{ r: 1 }}
                name="Uncorrected Inner (cm/hr)"
                strokeWidth={2}
                connectNulls
              />
            )}
            {visibleFields.uncorrected_outer && (
              <Line 
                type="monotone" 
                dataKey="uncorrected_outer" 
                stroke="#82ca9d" 
                activeDot={{ r: 3 }}
                dot={{ r: 1 }}
                name="Uncorrected Outer (cm/hr)"
                strokeWidth={2}
                connectNulls
              />
            )}
            {visibleFields.battery_voltage && (
              <Line 
                type="monotone" 
                dataKey="battery_voltage" 
                stroke="#ffc658" 
                activeDot={{ r: 3 }}
                dot={{ r: 1 }}
                name="Battery Voltage (V)"
                strokeWidth={2}
                connectNulls
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SensorVisualization;
